import type { TriggerReportJobRequestPayload } from '@vy/api-definition';
import { Box, IconButton, ToggleButton, Tooltip, Typography } from '@mui/material';
import { VyIcon, VyMonthYearRangePicker, type MonthYearRange } from '@vy/ui-design-system';
import { vyDate } from '@vy/utils';
import noop from 'lodash/noop';
import type { ReactNode } from 'react';
import { VyDatePicker } from '../../../components/new-design/date-picker/VyDatePicker';
import { VyTableToolbar } from '../../../components/new-design/table/vy-table-toolbar/vy-table-toolbar';
import {
  VyTableFilter,
  type FilterField,
  type FilterLineType,
} from '../../../components/new-design/vyTableFilter';
import { useTriggerReportJob } from '../../../hooks/requests/use-trigger-report-job';
import { runMutation } from '../../../utils/vy-mutation';
import { splitMonthYearRange, toMonthYearRange } from './month-year-range-bridge';
import {
  reportChartToggleButtonSx,
  reportDatePickerSlotProps,
  reportFilterButtonWrapperSx,
  reportIconButtonSx,
  reportSearchContainerSx,
  reportSearchInputSx,
} from './report-toolbar-styles';

type RevenueReportToolbarProps = {
  // Search
  onSearchChange?: (value: string) => void;
  searchValue?: string;
  searchPlaceholder?: string;
  searchLabel?: string;
  hideSearch?: boolean;

  // Date range
  hideDateRange?: boolean;
  fromDate?: Date | null;
  onFromDateChange?: (date: Date | null) => void;
  toDate?: Date | null;
  onToDateChange?: (date: Date | null) => void;
  /**
   * Render the new single-field month-year range picker.
   * Default false keeps the legacy two-picker UI for unmigrated reports.
   */
  useDateRangePicker?: boolean;

  // Filters
  filterFields?: FilterField[];
  filters?: FilterLineType[];
  onFiltersChange?: (filters: FilterLineType[]) => void;
  hideFilters?: boolean;

  // Chart toggle
  showChart?: boolean;
  onShowChartChange?: (show: boolean) => void;
  hideChartToggle?: boolean;

  // Trailing slot + export
  trailingActions?: ReactNode;
  onExport: () => void;

  // Refresh — provide either `refreshPayload` (trigger-job flow) OR `onRefresh`+`isRefreshing` (direct).
  refreshPayload?: TriggerReportJobRequestPayload;
  onRefresh?: () => void;
  isRefreshing?: boolean;
};

export const RevenueReportToolbar: React.FC<RevenueReportToolbarProps> = ({
  onSearchChange,
  searchValue,
  searchPlaceholder = 'Search',
  searchLabel,
  hideSearch = false,
  hideDateRange = false,
  hideChartToggle = false,
  fromDate = null,
  onFromDateChange,
  toDate = null,
  onToDateChange,
  useDateRangePicker = false,
  filterFields = [],
  filters = [],
  onFiltersChange,
  hideFilters = false,
  showChart = false,
  onShowChartChange,
  onExport,
  refreshPayload,
  onRefresh,
  isRefreshing = false,
  trailingActions,
}) => {
  const triggerReportJobMutation = useTriggerReportJob();

  const handleTriggerReport = async () => {
    if (!refreshPayload) return;
    await runMutation(triggerReportJobMutation, refreshPayload, {
      pending: 'Refreshing report...',
      success: 'Report refreshed',
      error: 'Failed to refresh report',
    });
  };

  const handleDateRangeChange = (range: MonthYearRange) => {
    const { fromDate: nextFromDate, toDate: nextToDate } = splitMonthYearRange(range);
    onFromDateChange?.(nextFromDate);
    onToDateChange?.(nextToDate);
  };

  const showRefresh = Boolean(refreshPayload || onRefresh);
  const refreshDisabled = refreshPayload ? triggerReportJobMutation.isLoading : isRefreshing;
  const handleRefreshClick = refreshPayload ? handleTriggerReport : onRefresh;

  return (
    <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
      {!hideSearch && (
        <Box
          sx={[
            { display: 'flex', flexDirection: 'column' },
            ...(Array.isArray(reportSearchContainerSx) ? reportSearchContainerSx : [reportSearchContainerSx]),
          ]}
        >
          {searchLabel && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {searchLabel}
            </Typography>
          )}
          <VyTableToolbar.SearchInput
            showLabel={false}
            onChange={(event) => onSearchChange?.(event.target.value)}
            defaultValue={searchValue ?? ''}
            placeholder={searchPlaceholder}
            sx={reportSearchInputSx}
          />
        </Box>
      )}
      {!hideDateRange &&
        (useDateRangePicker ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
            <VyMonthYearRangePicker
              value={toMonthYearRange(fromDate, toDate)}
              onChange={handleDateRangeChange}
            />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
              <VyDatePicker
                value={fromDate}
                onChange={onFromDateChange ?? noop}
                views={['month', 'year']}
                openTo="month"
                format="MMM YYYY"
                disableManualInput
                maxDate={toDate ? vyDate(toDate) : undefined}
                slotProps={reportDatePickerSlotProps}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
              <VyDatePicker
                value={toDate}
                onChange={onToDateChange ?? noop}
                views={['month', 'year']}
                openTo="month"
                format="MMM YYYY"
                disableManualInput
                minDate={fromDate ? vyDate(fromDate) : undefined}
                slotProps={reportDatePickerSlotProps}
              />
            </Box>
          </>
        ))}
      {!hideFilters && (
        <Box sx={reportFilterButtonWrapperSx}>
          <VyTableFilter
            fields={filterFields}
            defaultFilters={filters}
            onFiltersChange={onFiltersChange ?? noop}
          />
        </Box>
      )}
      {!hideChartToggle && (
        <ToggleButton
          value="chart"
          selected={showChart}
          onChange={() => onShowChartChange?.(!showChart)}
          sx={reportChartToggleButtonSx}
        >
          <VyIcon icon="BarChart" style={{ transform: 'scale(0.8)' }} />
          Chart
        </ToggleButton>
      )}
      {trailingActions}
      <Box sx={{ flexGrow: 1, minWidth: 16 }} />
      {showRefresh && (
        <Tooltip title="Refresh report">
          <span>
            <IconButton
              aria-label="Refresh report"
              onClick={handleRefreshClick}
              disabled={refreshDisabled}
              sx={reportIconButtonSx}
            >
              <VyIcon icon="Refresh" style={{ transform: 'scale(0.65)' }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <IconButton onClick={onExport} sx={reportIconButtonSx} aria-label="Export to XLSX">
        <VyIcon icon="Download" style={{ transform: 'scale(0.65)' }} />
      </IconButton>
    </Box>
  );
};
